/**
 * Parses partner/BaaS PIX error payloads into a single clean PT message.
 * Avoids dumping raw JSON in password-step / toast error boxes.
 */

const SCHEDULE_WINDOW_CODE = "pix.payment.outside.schedule.window";

const CODE_MESSAGES = {
  [SCHEDULE_WINDOW_CODE]:
    "Pagamento PIX fora da grade de horário permitida (06:30 às 18:29).",
};

const looksLikeJsonObject = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed.startsWith("{") && trimmed.includes("}");
};

const tryParseJsonObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }
  if (typeof value !== "string" || !looksLikeJsonObject(value)) {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

/**
 * @param {unknown} raw - API error body, information/detail string, or nested partner JSON
 * @returns {{ code: string|null, message: string, description: string|null }}
 */
export const parsePartnerPixError = (raw) => {
  const root = tryParseJsonObject(raw) || (raw && typeof raw === "object" ? raw : null);

  const nested =
    tryParseJsonObject(root?.information)
    || tryParseJsonObject(root?.detail)
    || tryParseJsonObject(root?.description)
    || tryParseJsonObject(root?.baas_body)
    || tryParseJsonObject(root?.message)
    || null;

  const code = firstNonEmpty(
    nested?.code,
    root?.code,
    root?.error,
    typeof raw === "string" && !looksLikeJsonObject(raw) ? null : null,
  );

  const partnerMessage = firstNonEmpty(
    nested?.message,
    root?.message,
    !looksLikeJsonObject(root?.information) ? root?.information : null,
    !looksLikeJsonObject(root?.detail) ? root?.detail : null,
  );

  const mapped = code && CODE_MESSAGES[code] ? CODE_MESSAGES[code] : null;
  const cleanPartner =
    partnerMessage && !looksLikeJsonObject(partnerMessage) ? partnerMessage : null;
  const message =
    mapped
    || cleanPartner
    || firstNonEmpty(root?.title, typeof raw === "string" && !looksLikeJsonObject(raw) ? raw : null)
    || "Não foi possível concluir a transferência.";

  // Prefer a single clean line in the red error box (title only).
  return {
    code: code || null,
    message,
    description: null,
  };
};

export const isPixScheduleWindowError = (codeOrPayload) => {
  if (typeof codeOrPayload === "string") {
    return codeOrPayload === SCHEDULE_WINDOW_CODE
      || codeOrPayload.includes(SCHEDULE_WINDOW_CODE);
  }
  const parsed = parsePartnerPixError(codeOrPayload);
  return parsed.code === SCHEDULE_WINDOW_CODE;
};
